import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import LegendCard from "@/components/feature/LegendCard";
import { legendStories } from "@/mocks/stories";

const categories = ["全部", "真相求证", "旧事档案", "夜间传说"] as const;

export default function ArchivePage() {
  const [params, setParams] = useSearchParams();
  const activeCat = params.get("cat") || "全部";
  const [keyword, setKeyword] = useState("");
  const [query, setQuery] = useState("");

  useEffect(() => {
    document.title = "怪谈档案库 · 校园怪谈求证与学校未解传闻 · 校园爆料";
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  const stories = useMemo(() => {
    let list = activeCat === "全部" ? legendStories : legendStories.filter((s) => s.category === activeCat);
    if (query.trim()) {
      const q = query.trim();
      list = list.filter(
        (s) => s.title.includes(q) || s.excerpt.includes(q) || s.tagline.includes(q) || s.place.includes(q)
      );
    }
    return list;
  }, [activeCat, query]);

  const setCat = (c: string) => {
    if (c === "全部") setParams({});
    else setParams({ cat: c });
  };

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setQuery(keyword.trim());
  };

  return (
    <>
      {/* 档案库首屏横幅 */}
      <section className="relative flex min-h-[46vh] items-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=a%20stack%20of%20old%20brown%20paper%20case%20files%20and%20a%20dim%20desk%20lamp%20in%20a%20dark%20archive%20room%2C%20single%20warm%20light%2C%20dust%20in%20air%2C%20ink%20wash%20editorial%20illustration%2C%20deep%20brown%20amber%20black%20tones&width=1920&height=820&seq=archive-banner-03&orientation=landscape"
          alt="校园爆料怪谈档案库横幅"
          className="absolute inset-0 h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-night-900/70 via-night-900/70 to-night-900"></div>
        <div className="relative z-10 mx-auto w-full max-w-7xl px-4 py-16 md:px-6">
          <nav className="mb-4 text-sm text-parchment-300/60">
            首页 <i className="ri-arrow-right-s-line align-middle"></i> 怪谈档案库
          </nav>
          <h1 className="serif-head text-4xl font-black text-parchment-100 md:text-5xl">怪谈档案库</h1>
          <p className="mt-4 max-w-2xl text-lg leading-relaxed text-parchment-300/85">
            校园怪谈是真的吗？这里收录了我们完成求证的学校未解传闻与校园旧事档案。每一则，都标注了求证结论与依据。
          </p>
        </div>
      </section>

      {/* 筛选与列表 */}
      <section className="bg-night-900 py-12 md:py-16">
        <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
          <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              {categories.map((c) => {
                const active = activeCat === c;
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setCat(c)}
                    className={`whitespace-nowrap rounded-full border px-4 py-2 text-sm transition ${
                      active
                        ? "border-cinnabar-500 bg-cinnabar-600 text-parchment-100"
                        : "border-night-500/60 bg-night-850 text-parchment-300/75 hover:border-amberlight-400/50 hover:text-parchment-100"
                    }`}
                  >
                    {c}
                  </button>
                );
              })}
            </div>

            <form onSubmit={submitSearch} className="flex w-full max-w-md items-center gap-2 lg:w-auto">
              <div className="relative flex-1 lg:w-72">
                <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-parchment-300/50"></i>
                <input
                  type="text"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索传闻、地点或关键词…"
                  className="w-full rounded-md border border-night-600/60 bg-night-850 py-2.5 pl-10 pr-3 text-sm text-parchment-100 placeholder:text-parchment-300/40 focus:border-amberlight-400/60 focus:outline-none"
                />
              </div>
              <button
                type="submit"
                className="whitespace-nowrap rounded-md border border-amberlight-400/50 bg-amberlight-400/10 px-5 py-2.5 text-sm text-amberlight-300 transition hover:bg-amberlight-400/20"
              >
                搜索
              </button>
            </form>
          </div>

          {stories.length > 0 ? (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {stories.map((s) => (
                <LegendCard key={s.id} story={s} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center rounded-lg border border-night-600/50 bg-night-850 px-6 py-20 text-center">
              <i className="ri-folder-forbid-line text-4xl text-parchment-300/40"></i>
              <p className="mt-4 text-parchment-200">没有找到匹配的档案</p>
              <p className="mt-1 text-sm text-parchment-300/60">换个关键词，或清空筛选条件再试试</p>
            </div>
          )}
        </div>
      </section>
    </>
  );
}